// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState } from 'react';
import { Modal, TextArea } from '@carbon/react';
import type { ChecklistItem } from '../../../types';

interface BypassModalProps {
  item: ChecklistItem | null;
  onSubmit: (_item: ChecklistItem, _reason: string) => void;
  onClose: () => void;
}

/**
 * Reason capture for bypassing a checklist item. A reason is always required
 * (the primary action stays disabled while empty) and the acting user is
 * recorded with it, per the coverage contract.
 */
export default function BypassModal({ item, onSubmit, onClose }: BypassModalProps) {
  const [reason, setReason] = useState('');

  const handleClose = () => {
    setReason('');
    onClose();
  };

  return (
    <Modal
      open={item !== null}
      modalHeading={item ? `Bypass "${item.expected_doc_type}"` : ''}
      primaryButtonText="Bypass item"
      secondaryButtonText="Cancel"
      primaryButtonDisabled={reason.trim().length === 0}
      onRequestClose={handleClose}
      onRequestSubmit={() => {
        if (item && reason.trim()) {
          onSubmit(item, reason.trim());
          setReason('');
        }
      }}
      size="sm"
    >
      <p className="obw-modal-note">
        A bypassed item stops blocking submission but stays on the record with your name and this
        reason. It reopens automatically if a matching document arrives later.
      </p>
      <TextArea
        id="bypass-reason"
        labelText="Reason (required)"
        placeholder="e.g. Vendor SOP pending delivery"
        value={reason}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReason(e.target.value)}
        rows={3}
      />
    </Modal>
  );
}
